import {TeamReviewEvidence} from "./TeamReviewContext";
import ReassessmentAdaptation from "./ReassessmentAdaptation";
import {preferenceShares, changePreferenceShare} from "../helpers/reassessmentPreferences";
import {assessmentEdit} from "../helpers/reassessmentRecord";
import ReassessmentPreferences from "./ReassessmentPreferences";
import { conversionSeasonPolicy } from "../helpers/conversionSeasonPolicy";
import React, { useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { futureSummary } from "../helpers/teamAnalysis";
import { homeAdvantage } from "../helpers/teamSettingsImpact";
import { goalFieldLabel } from "../helpers/teamSettingValues";
import {
  reviewProfiles,
  profileWeights,
  resultReassessment,
  statisticReassessment,
  conversionReassessment,
  settingDirection,
  pointContext,
} from "../helpers/teamReassessment";

export default function TeamReassessment({
  data,
  teamId,
  en,
  mode,
  names,
  logo,
  statName,
  busy,
  recent: initialRecent = 8,
  onPreview,
  onApply,
  onDrawImpact,
}) {
  const t = (d, e) => (en ? e : d),
    fmt = (v, d = 2) =>
      Number.isFinite(v)
        ? v.toLocaleString(en ? "en-GB" : "de-DE", {
            minimumFractionDigits: d,
            maximumFractionDigits: d,
            useGrouping: false,
          })
        : "—";
  const [recent, setRecent] = useState(initialRecent);
  const [open, setOpen] = useState(false),
    [profile, setProfile] = useState("balanced"),
    [pace, setPace] = useState(25),
    [statPace, setStatPace] = useState(25),
    [weights, setWeights] = useState(() =>
      profileWeights("balanced", {}, () => 0.5)
    ),
    [rows, setRows] = useState(null),
    [impact, setImpact] = useState(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState("");
  const labels = {
    should: t("Saisonleistung", "Season performance"),
    real: t("Ergebnisse", "Results"),
    market: t("Markt", "Market"),
    recent: t("Jüngste Leistung", "Recent performance"),
    other: t("Statistikmodell", "Statistics model"),
    previous: t("Vorsaison", "Previous season"),
    external: "xGoals",
  };
  const generation = useRef(0),
    version = useRef(0),
    preferences = useRef({});
  const [notice, setNotice] = useState("");
  const [preserve, setPreserve] = useState(false);
  const summaryRef = useRef(null);
  const [drawEffect, setDrawEffect] = useState(null);
  const seasonPolicy = conversionSeasonPolicy(data.state, teamId, preserve);
  const sourcePoints = pointContext(data, teamId, "result");
  const edits = (r) => [
    ...(r.statistics.find(v => v.assessment) ? [assessmentEdit(teamId, r.statistics.find(v => v.assessment).assessment)] : []),
    ...(seasonPolicy
      ? [
          {
            team_id: Number(teamId),
            group: "conversion_policy",
            field: "preserve",
            value: preserve ? 1 : 0,
          },
        ]
      : []),
    ...r.statistics.map((v) => ({
      team_id: Number(teamId),
      group: v.group,
      statistic_id: v.statistic_id,
      field: v.field,
      value: v.value,
    })),
  ];
  const resultRow = (r) => {
    let next = { ...data.result_teams[teamId] };
    for (const v of r.result) next[v.field] = v.value;
    return futureSummary(next);
  };
  async function preview(r) {
    const token = ++generation.current;
    setPending(true);
    setError("");
    setImpact(null);
    setDrawEffect(null);
    try {
      const p = await onPreview(edits(r));
      if (token === generation.current)
        setImpact({
          statistics: p?.ranking?.find(
            (x) => Number(x.team_id) === Number(teamId)
          ),
          result: resultRow(r),
        });
    } catch (e) {
      if (token === generation.current) setError(e.message);
    } finally {
      if (token === generation.current) setPending(false);
    }
  }
  function generate() {
    setNotice("");
    try {
      const result = resultReassessment(data, teamId, weights, pace, recent);
      const statistics = [
        ...statisticReassessment(data, teamId, statPace, preserve),
        ...conversionReassessment(data, teamId, statPace, preserve),
      ];
      const r = { result: result.rows, statistics, version: ++version.current };
      setRows(r);
      preview(r);
    } catch (e) {
      setError(e.message);
    }
  }
  function change(group, index, value) {
    setRows((r) => ({
      ...r,
      [group]: r[group].map((v, i) =>
        i === index
          ? {
              ...v,
              value: value === "" ? null : Number(value.replace(",", ".")),
            }
          : v
      ),
    }));
    setImpact(null);
    setDrawEffect(null);
  }
  const valid =
    rows &&
    [...rows.result, ...rows.statistics].every(
      (v) =>
        Number.isFinite(v.value) &&
        v.value >= (v.group === "statistic" ? 0 : Number.EPSILON) &&
        (v.statistic_id !== "45" || v.value <= 1)
    );
  const delta = (field, before, after, group = "") => {
    const direction = settingDirection(field, before, after, group);
    return (
      <span
        className={
          direction > 0 ? "tw-positive" : direction < 0 ? "tw-negative" : ""
        }
      >
        {Number.isFinite(after) && Number.isFinite(before)
          ? `${after - before > 0 ? "+" : ""}${fmt(after - before)}`
          : "—"}
        {direction === 1
          ? t(" · besser", " · stronger")
          : direction === -1
          ? t(" · schwächer", " · weaker")
          : ""}
      </span>
    );
  };
  const input = (r, group, i) => (
    <input
      aria-label={`${
        group === "result"
          ? t("Ergebnismodell", "Results model")
          : r.group === "conversion"
          ? "Conversion"
          : statName(r.statistic_id)
      } · ${goalFieldLabel(r.field, en)}`}
      type="text"
      inputMode="decimal"
      defaultValue={fmt(
        r.value *
          (r.group === "conversion" || r.statistic_id === "45" ? 100 : 1)
      )}
      key={`${group}-${i}-${profile}-${pace}-${statPace}-${open}-${
        rows?.version || ""
      }`}
      onChange={(e) => {
        const scale =
          r.group === "conversion" || r.statistic_id === "45" ? 100 : 1;
        const value = e.target.value;
        change(
          group,
          i,
          value === "" ? "" : String(Number(value.replace(",", ".")) / scale)
        );
      }}
      disabled={pending || busy}
    />
  );
  function configure(fn) {
    generation.current++;
    setPending(false);
    fn();
    setRows(null);
    setImpact(null);
    setDrawEffect(null);
  }
  return (
    <>
      <section className="tw-reassessment-entry">
        {notice && <Alert severity="info">{notice}</Alert>}
        <Button
          variant="contained"
          onClick={() => {
            setPreserve(
              data.state?.teams?.[teamId]?.conversion_season_policy
                ?.preserve === true
            );
            setOpen(true);
            setError("");
            setRows(null);
            setImpact(null);
            setDrawEffect(null);
            try {
              const p = JSON.parse(
                localStorage.getItem("betty.team-analysis.reassessment.v1") ||
                  localStorage.getItem(
                    "betty.team-analysis.personal-weights.v2.result"
                  )
              );
              if (p?.weights) {
                preferences.current = p.weights;
                setWeights(p.weights);
                setPace(p.pace ?? p.adapt ?? 25);
                setStatPace(p.statPace ?? 25);
                if (p.recent === 6 || p.recent === 8) setRecent(p.recent);
                setProfile(p.profile ?? "balanced");
              }
            } catch {}
          }}
        >
          {t(
            "Team neu bewerten · beide Modelle",
            "Reassess team · both models"
          )}
        </Button>
        <p>
          {t(
            "1. Vorschläge einholen → 2. Einzelwerte prüfen und bearbeiten → 3. Als Entwurf übernehmen → 4. Vorschau → Änderungen speichern. Erst dann sind die neuen Settings aktiv.",
            "1. Request proposals → 2. Review and edit values → 3. Apply as draft → 4. Preview → Save changes. Only then are the new settings active."
          )}
        </p>
      </section>
      <Dialog
        open={open}
        onClose={() => {
          if (!pending) {
            generation.current++;
            setOpen(false);
          }
        }}
        maxWidth="xl"
        fullWidth
        sx={{ zIndex: 1800 }}
        PaperProps={{ className: `team-analysis ta-${mode}` }}
      >
        <DialogTitle>
          {logo(teamId)} {names(teamId)} · {t("Neu bewerten", "Reassessment")}
        </DialogTitle>
        <DialogContent>
          <TeamReviewEvidence data={data} teamId={teamId} name={names(teamId)} en={en}/>
          <Alert severity="info">
            {t(
              "Hier entsteht zunächst ein Vorschlag aus den gespeicherten Ausgangswerten. Vorhandene Entwürfe dieses Teams werden erst beim Übernehmen ersetzt. Es wird nichts automatisch gespeichert.",
              "This creates a proposal from saved baseline values. Existing drafts for this team are only replaced when you apply it. Nothing is saved automatically."
            )}
          </Alert>
          <details open><summary>{t("Persönliche Vorgaben · Fragen beantworten oder ändern", "Personal preferences · answer or change questions")}</summary>
              <ReassessmentPreferences en={en} onApply={p => {setWeights(p.weights); setPace(p.pace); setStatPace(p.statPace); setProfile("balanced"); setRecent(p.recent);  setNotice(t("Persönliche Vorgaben übernommen. Vorschläge jetzt neu berechnen.", "Preferences applied. Recalculate proposals now.")); setRows(null); setImpact(null);}} />
            </details>
            <fieldset className="tw-conversion-policy">
            <legend>
              {t("Conversion-Eigenheit erhalten", "Retain conversion characteristics")}
            </legend>
            <label>
              <input
                type="checkbox"
                checked={preserve}
                disabled={pending || busy}
                onChange={(e) => configure(() => setPreserve(e.target.checked))}
              />
              {t(
                "Etabliertes Spitzenteam: dauerhafte Conversion-Eigenheit erhalten",
                "Established top team: retain lasting conversion characteristics"
              )}
            </label>
            <p>
              {t(
                "Angehakt: Der gespeicherte Abstand zum Ligamittel bleibt bei der aktuellen Conversion-Neubewertung vollständig erhalten. Ohne Auswahl bleiben 25 %. Die belegte Saisonleistung wird jeweils mit n/(n+40) berücksichtigt; anschließend wird ligaweit normiert. Die Auswahl gilt außerdem für die vorhandene Saisonwechsel-Regel.",
                "Selected: the saved difference from the league mean is fully retained in the current conversion review. Otherwise 25% is retained. Covered season performance is weighted by n/(n+40), followed by league normalization. The selection also applies to the existing season-transition policy."
              )}
            </p>
            {!seasonPolicy && (
              <Alert severity="warning">
                {t(
                  "Ursprüngliche Saisonbasis fehlt. Die aktuelle Conversion-Bewertung verwendet das aktuelle Ligamittel und den gespeicherten Teamwert.",
                  "Original season baseline missing. Current conversion reassessment uses the current league mean and saved team value."
                )}
              </Alert>
            )}
          </fieldset>
          <div className="tw-reassessment-controls">
            <label>
              {t("Beratungsprofil", "Adviser profile")}{" "}
              <select
                disabled={pending || busy}
                value={profile}
                onChange={(e) =>
                  configure(() => {
                    setProfile(e.target.value);
                    setWeights(
                      profileWeights(e.target.value, preferences.current)
                    );
                    setPace(reviewProfiles[e.target.value].pace);
                    setStatPace(reviewProfiles[e.target.value].pace);
                  })
                }
              >
                {Object.entries(reviewProfiles).map(([k, p]) => (
                  <option key={k} value={k}>
                    {en ? p.en : p.name}
                  </option>
                ))}
              </select>
            </label>
            <Button
              disabled={pending || busy}
              onClick={() =>
                configure(() =>
                  setWeights(profileWeights(profile, preferences.current))
                )
              }
            >
              {t(
                "Neuen Profilvorschlag einholen",
                "Request new profile suggestion"
              )}
            </Button>
          </div>
          <Button
            onClick={() => {
              try {
                localStorage.setItem(
                  "betty.team-analysis.reassessment.v1",
                  JSON.stringify({ weights, pace, statPace, profile, recent })
                );
                preferences.current = weights;
                setNotice(
                  t(
                    "Profil auf diesem Gerät für alle Teams und Ligen gespeichert.",
                    "Profile saved on this device for all teams and leagues."
                  )
                );
              } catch {
                setError(
                  t(
                    "Profil konnte nicht gespeichert werden.",
                    "Could not save profile."
                  )
                );
              }
            }}
          >
            {t("Profil speichern", "Save profile")}
          </Button>
          <details>
            <summary>
              {t("Quellgewichte selbst einstellen", "Adjust source weights")}
            </summary>
            {Object.entries(weights).map(([k, v]) => (
              <label className="tw-reassessment-weight" key={k}>
                {labels[k]}{" "}
                <input
                  disabled={pending || busy}
                  type="number"
                  min="0"
                  max="100"
                  aria-label={`${t(
                    "Neubewertung Gewicht",
                    "Reassessment weight"
                  )} ${labels[k]}`}
                  value={Number((preferenceShares(weights)[k] || 0).toFixed(2))}
                  onChange={(e) =>
                    configure(() =>
                      setWeights(changePreferenceShare(weights, k, e.target.value))
                    )
                  }
                /> %
              </label>
            ))}
            <p><strong>{t("Summe Quellenanteile", "Total source shares")}: {Object.values(weights).some(v => v > 0) ? "100" : "0"} %</strong></p>
            <small>
              {t(
                "Die verfügbaren Quellen werden anteilig auf 100 % normiert. Fehlende Daten erhalten kein wirksames Gewicht. Profile verschieben die Quellenanteile; alle Gewichte bleiben bearbeitbar.",
                "Available sources are normalized to 100%. Missing data receives no effective weight. Profiles shift source shares; all weights remain editable."
              )}
            </small>
          </details>
          <ReassessmentAdaptation en={en} pace={pace} statPace={statPace} />
          <Button
            variant="contained"
            disabled={pending || busy}
            onClick={generate}
          >
            {t(
              "Änderungen für beide Modelle vorschlagen",
              "Propose changes for both models"
            )}
          </Button>
          {sourcePoints.count > 0 && (
            <p>
              {t("Punkte berücksichtigt", "Points considered")}:{" "}
              {fmt(sourcePoints.actual, 0)} {t("erzielt,", "actual,")}{" "}
              {fmt(sourcePoints.expected, 2)} {t("erwartet", "expected")} ·{" "}
              {sourcePoints.count}{" "}
              {t(
                "gemeinsame Spiele. Bessere Punktausbeute als erwartet mildert eine negative Korrektur aus der Torbilanz.",
                "shared matches. More points than expected soften an adverse correction from the goal record."
              )}
            </p>
          )}
          {rows && (
            <>
              <section
                className="tw-reassessment-summary"
                ref={summaryRef}
                tabIndex={-1}
              >
                <h3>
                  {t(
                    "Auswirkungen beider Modelle · vorher / nachher",
                    "Both models · before / after"
                  )}
                </h3>
                <div className="ta-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("Modell", "Model")}</th>
                        <th>{t("Spielstärke", "Strength")}</th>
                        <th>{t("Torniveau", "Goal level")}</th>
                        <th>
                          {t(
                            "Heimvorteil · Quotient",
                            "Home advantage · ratio"
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {["result", "statistics"].map((m) => {
                        const before = futureSummary(
                          m === "result"
                            ? data.result_teams[teamId]
                            : data.ranking.find(
                                (x) => Number(x.team_id) === Number(teamId)
                              )
                        );
                        const after = impact?.[m];
                        return (
                          <tr key={m}>
                            <th>
                              {m === "result"
                                ? t("Ergebnismodell", "Results model")
                                : t("Statistikmodell", "Statistics model")}
                            </th>
                            {[
                              "total_strength",
                              "total_goals",
                              "home_advantage",
                            ].map((k) => {
                              const a =
                                  k === "home_advantage"
                                    ? homeAdvantage(before)
                                    : before?.[k],
                                b =
                                  k === "home_advantage"
                                    ? homeAdvantage(after)
                                    : after?.[k];
                              return (
                                <td key={k}>
                                  {fmt(a)} → <b>{fmt(b)}</b>
                                  <br />
                                  {delta(k, a, b)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
              <h3>
                {t(
                  "Ergebnismodell · alle Einzelwerte",
                  "Results model · all individual values"
                )}
              </h3>
              <div className="ta-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>{t("Wert", "Value")}</th>
                      <th>
                        {t(
                          "Gespeichert · zu ändern",
                          "Saved · editing baseline"
                        )}
                      </th>
                      <th>
                        {t("Vorschlag · bearbeitbar", "Proposal · editable")}
                      </th>
                      <th>Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.result.map((r, i) => (
                      <tr key={r.field}>
                        <th>{goalFieldLabel(r.field, en)}</th>
                        <td>{fmt(r.before)}</td>
                        <td>{input(r, "result", i)}</td>
                        <td>{delta(r.field, r.before, r.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <h3>
                {t(
                  "Statistikmodell · alle Einzelwerte",
                  "Statistics model · all individual values"
                )}
              </h3>
              <div className="ta-scroll">
                <table>
                  <thead>
                    <tr>
                      {[
                        t("Statistik / Spielort", "Statistic / venue"),
                        t(
                          "Gespeichert · zu ändern",
                          "Saved · editing baseline"
                        ),
                        t("Bisher erwartet", "Expected so far"),
                        t("Erspielt", "Observed"),
                        t("Vorsaison", "Previous season"),
                        t("Vorschlag · bearbeitbar", "Proposal · editable"),
                        "Δ",
                        t("Spiele", "Matches"),
                      ].map((x) => (
                        <th key={x}>{x}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.statistics.map((r, i) => {
                      const scale =
                        r.group === "conversion" || r.statistic_id === "45"
                          ? 100
                          : 1;
                      return (
                        <tr key={`${r.statistic_id}-${r.field}`}>
                          <th>
                            {r.group === "conversion"
                              ? "Conversion (%)"
                              : statName(r.statistic_id)}{" "}
                            · {goalFieldLabel(r.field, en)}
                            {r.group === "conversion" && (
                              <small style={{ display: "block" }}>
                                {t("Ligastandard", "League standard")}:{" "}
                                {fmt(
                                  Number.isFinite(r.standard)
                                    ? r.standard * 100
                                    : null
                                )}{" "}
                                %
                                <br />
                                {t("Saisonstart → gespeichert", "Season start → saved")}:{" "}
                                {fmt(
                                  Number.isFinite(r.original)
                                    ? r.original * 100
                                    : null
                                )}{" "}
                                % →{" "}
                                {fmt(
                                  Number.isFinite(r.baseline)
                                    ? r.baseline * 100
                                    : null
                                )}{" "}
                                %
                              </small>
                            )}
                          </th>
                          <td
                            style={{
                              fontWeight: 900,
                              background: "rgba(70,170,220,.18)",
                              borderLeft: "4px solid #65caff",
                            }}
                          >
                            {fmt(r.before * scale)}
                            {scale === 100 ? " %" : ""}
                          </td>
                          <td>
                            {fmt(
                              Number.isFinite(r.range?.expected)
                                ? r.range.expected * scale
                                : null
                            )}
                            {scale === 100 ? " %" : ""}
                          </td>
                          <td>
                            {fmt(
                              Number.isFinite(r.range?.observed)
                                ? r.range.observed * scale
                                : null
                            )}
                            {scale === 100 ? " %" : ""}
                          </td>
                          <td>
                            {fmt(
                              Number.isFinite(r.previous)
                                ? r.previous * scale
                                : null
                            )}
                            {scale === 100 ? " %" : ""}
                          </td>
                          <td>
                            {input(r, "statistics", i)}
                            {scale === 100 ? " %" : ""}
                          </td>
                          <td>
                            {delta(
                              r.field,
                              r.before * scale,
                              r.value === null ? null : r.value * scale,
                              r.group === "conversion"
                                ? "conversion"
                                : r.statistic_id
                            )}
                          </td>
                          <td>{r.range?.count ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {onDrawImpact && (
                <section>
                  <Button
                    disabled={pending || busy || !valid}
                    onClick={async () => {
                      setPending(true);
                      setError("");
                      try {
                        setDrawEffect(await onDrawImpact(rows.result));
                      } catch (e) {
                        setError(e.message);
                      } finally {
                        setPending(false);
                      }
                    }}
                  >
                    {t(
                      "Remiswahrscheinlichkeitsänderung schätzen",
                      "Estimate draw probability change"
                    )}
                  </Button>
                  {drawEffect && (
                    <p>
                      {t("Geschätzte Änderung", "Estimated change")}:{" "}
                      {drawEffect.unavailable
                        ? "—"
                        : `${
                            drawEffect.delta_percentage_points > 0 ? "+" : ""
                          }${fmt(drawEffect.delta_percentage_points, 2)}`}{" "}
                      {t(
                        "Prozentpunkte · gegen",
                        "percentage points · against"
                      )}{" "}
                      {names(drawEffect.opponent_id)}.{" "}
                      {t(
                        "Mittel aus Heim/Auswärts; nur der eigene Remisfaktor ändert sich.",
                        "Average of home/away; only this team’s draw factor changes."
                      )}
                    </p>
                  )}
                  <small>
                    {t(
                      "Mehr Remisen als erwartet senken den Faktor; weniger erhöhen ihn. Nach Spielzahl gedämpfte Anpassung, keine Signifikanzschwelle.",
                      "More draws than expected lower the factor; fewer raise it. Adjustment damped by match count, no significance threshold."
                    )}
                  </small>
                </section>
              )}
              <p>
                {t(
                  "Conversion = Ligamittel + Erhaltungsanteil × (gespeichert − Ligamittel) + n/(n+40) × (erspielt − Ligamittel). Erhaltungsanteil: angehakt 100 %, sonst 25 %. n zählt belegte Spiele der jeweiligen Heim-/Auswärtsseite. Kein zusätzlicher Anpassungsstärke-Faktor und keine feste Begrenzung. Eingaben sind vor Normierung; die Statistikmodell-Auswirkung oben enthält bereits die Liga-Normierung. Vor dem Speichern zeigt die Vorschau die endgültigen Werte und Season Parameters. Wiederholte Vorschläge neutralisieren nicht erneut.",
                  "Conversion = league mean + retention share × (saved − league mean) + n/(n+40) × (observed − league mean). Retention share: selected 100%, otherwise 25%. n counts covered games for the respective venue. No additional progression factor or fixed cap. Inputs are before normalization; the statistics-model impact above already includes league normalization. Before saving, Preview shows final values and season parameters. Repeated proposals do not neutralize again."
                )}
              </p>
              {!impact && (
                <Alert severity="info">
                  {t(
                    "Nach einer Eingabe: Auswirkungen aktualisieren. Erst die geprüften Werte können als Entwurf übernommen werden.",
                    "After an edit: refresh effects. Only reviewed values can be applied as a draft."
                  )}
                </Alert>
              )}
              <Button
                disabled={pending || busy || !valid}
                onClick={() => preview(rows)}
              >
                {t("Auswirkungen aktualisieren", "Refresh effects")}
              </Button>
            </>
          )}
          {notice && <Alert severity="success">{notice}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          {rows && (
            <Button
              onClick={() => {
                summaryRef.current?.scrollIntoView({ block: "start" });
                summaryRef.current?.focus({ preventScroll: true });
              }}
            >
              {t(
                "Vorher/Nachher beider Modelle",
                "Before/after of both models"
              )}
            </Button>
          )}
          <Button disabled={pending} onClick={() => setOpen(false)}>
            {t("Schließen · nichts übernehmen", "Close · apply nothing")}
          </Button>
          <Button
            variant="contained"
            disabled={!impact || pending || busy || !valid}
            onClick={async () => {
              setPending(true);
              try {
                if ((await onApply(rows.result, edits(rows))) !== false) {
                  setOpen(false);
                  setRows(null);
                  setImpact(null);
                  setDrawEffect(null);
                  setNotice(
                    t(
                      "Entwurf übernommen. Unten Vorschau öffnen, anschließend Änderungen speichern. Bis dahin sind die bisherigen Settings aktiv.",
                      "Draft applied. Open Preview below, then Save changes. Until then the previous settings remain active."
                    )
                  );
                }
              } catch (e) {
                setError(e.message);
              } finally {
                setPending(false);
              }
            }}
          >
            {t(
              "Geprüfte Vorschläge als Entwurf übernehmen",
              "Apply reviewed proposals as draft"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
